import { Fragment, useEffect, useMemo, useState } from 'react';
import { useAppState } from '../../app/state';
import { callAction } from '../../app/actions';
import * as Todos from '../../api/todos';
import type { ProjectsPayload, Todo } from '../../api/todos';
import { buildHiddenSets, canEditTodos, isPermHidden } from '../../lib/permissions';
import {
  assigneeMetrics,
  burnupRange,
  burnupSeries,
  groupByNode,
  projectSummary,
  rollup,
  scopeSet,
  scopeTree,
} from '../../lib/projects';
import { formatDue } from '../../lib/todos';
import type { NodeId } from '../../lib/types';
import { NodeChip } from '../../components/NodeChip';
import { projectText } from '../../i18n/projects';
import { todayKey } from '../popup/TodoSection';
import { Pie } from './Pie';
import { Avatars, Burnup, DueCell, ProgressBar, Schedule, StatusPill, TodoRows } from './projectsCells';

/**
 * THE PROJECTS TAB (V0.50.2) — how a project is going, read off the map and its to-dos.
 *
 * A project is a node marked as one plus its subtree plus the to-dos on all of them. The server
 * ships the FACTS (`GET /api/projects`: the marks this reader may see, and every to-do inside
 * their subtrees) and this tab joins them with the tree the client already holds — the
 * Documents tab's arrangement — through `lib/projects.ts`, which computes every figure on the
 * screen and is pinned by its own tests. Nothing here counts anything itself.
 *
 * THE READER IS A TO-DO MANAGER, who may hold no editing brick: the tab is drawn for
 * `VIEW_TODOS`, reached without edit mode (`openDashboard`'s second exemption), and shareable
 * as `/admin/projects`. Names come from the account directory, which V0.50.2 opened to the same
 * audience; until it has loaded, an id shows as `Account xxxxxx…` rather than as nothing.
 *
 * TWO VIEWS BEHIND ONE STRIP — Nodes and Assignees — the Logs tab's shape, at the user's
 * request ("in another tab, not side by side"). Both exist for every reader, so the strip is
 * always drawn. A row in either view opens to the to-dos beneath it, drawn by ONE renderer.
 *
 * THE ONE VERB ON THE DASHBOARD. Ticking a to-do from these rows was asked for and is allowed:
 * it is the popup's own `setTodoDone` — the same `PATCH /api/todos/:id/state`, the same
 * assignee-or-manager rule the server enforces — and a to-do's state is not a document
 * workflow transition, which is what "no tab offers workflow verbs" is about. The bump it makes
 * (`todosVersion`) is what re-reads this tab, and an open popup, without either knowing.
 *
 * `focusNodeId` is the hand-off from the popup's Progress button, read ONCE as the initial
 * selection (the mount is keyed on it); a plain click on the tab lands on the first project.
 * Refetches on `s.todosVersion` and the refresh button — NOT on the locale, because labels
 * come from `s.nodes` live.
 */
export function ProjectsTab({ focusNodeId, onLeave }: { focusNodeId: NodeId | null; onLeave: () => void }) {
  const s = useAppState();
  const locale = s.currentLocale;

  const [payload, setPayload] = useState<ProjectsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshSeq, setRefreshSeq] = useState(0);
  const [selected, setSelected] = useState<NodeId | null>(focusNodeId);
  const [view, setView] = useState<'nodes' | 'people'>('nodes');
  const [openNode, setOpenNode] = useState<NodeId | null>(null);
  const [openPerson, setOpenPerson] = useState<string | null>(null);
  const [openNotes, setOpenNotes] = useState<ReadonlySet<string>>(() => new Set());
  const [busyTodo, setBusyTodo] = useState<string | null>(null);
  const [tickError, setTickError] = useState<string | null>(null);

  /* ------------------------------------------------------------------ the read ---- */

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    Todos.fetchProjects().then(
      (p) => {
        if (!live) return;
        setPayload(p);
        setLoading(false);
      },
      (e: Error) => {
        if (!live) return;
        setError(e.message);
        setLoading(false);
      },
    );
    return () => {
      live = false;
    };
  }, [s.todosVersion, refreshSeq]);

  /* --------------------------------------------------------------- the figures ---- */

  // A feature may read the clock; the library it hands the day to may not.
  const today = todayKey();
  const hidden = useMemo(() => buildHiddenSets(s.perm, s.tagEdges), [s.perm, s.tagEdges]);
  // A UX layer over a list the server already scoped — the `MyTodos` rule.
  const projects = useMemo(
    () => (payload?.projects ?? []).filter((p) => !!s.nodes[p.nodeId] && !isPermHidden(p.nodeId, s.edges, hidden)),
    [payload, s.nodes, s.edges, hidden],
  );
  const allTodos = payload?.todos ?? [];
  const summaries = useMemo(
    () =>
      projects.map((p) => {
        const scope = scopeSet(s.edges, p.nodeId);
        return { project: p, summary: projectSummary(p, allTodos.filter((t) => scope.has(t.nodeId)), today) };
      }),
    [projects, allTodos, s.edges, today],
  );
  const selectedId = projects.some((p) => p.nodeId === selected) ? selected : (projects[0]?.nodeId ?? null);
  const current = selectedId ? (summaries.find((x) => x.project.nodeId === selectedId) ?? null) : null;
  const scope = selectedId ? scopeSet(s.edges, selectedId) : null;
  const scopeTodos = scope ? allTodos.filter((t) => scope.has(t.nodeId)) : [];
  const roll = selectedId ? rollup(s.edges, selectedId, scopeTodos, today) : null;
  const tree = selectedId && roll ? scopeTree(s.edges, selectedId).filter((r) => (roll.get(r.nodeId)?.subtree.total ?? 0) > 0) : [];
  const byNode = groupByNode(scopeTodos);
  const people = assigneeMetrics(scopeTodos, today);
  const range = current ? burnupRange(current.project, scopeTodos, today) : null;
  const points = range ? burnupSeries(scopeTodos, range.from, range.to) : [];
  const projectIds = new Set(projects.map((p) => p.nodeId));

  /* ---------------------------------------------------------------- the helpers ---- */

  const nameOf = (key: string): string => {
    if (key === 'unassigned') return projectText('unassigned', locale);
    const id = key.slice(2);
    const fallback = `${projectText('unnamedAccount', locale)} ${id.slice(0, 6)}…`;
    if (key.startsWith('g:')) return s.groupDirectory.find((g) => g.id === id)?.name ?? fallback;
    return s.userDirectory.find((u) => u.id === id)?.displayName ?? fallback;
  };
  const nodeLabelOf = (nodeId: string): string => s.nodes[nodeId]?.label ?? nodeId;

  /** May THIS reader tick THIS row? The server's rule, drawn — the popup's `mayTick`. */
  const mayTick = (t: Todo): boolean => {
    if (canEditTodos(s.perm)) return true;
    if (s.currentUser && t.assigneeUserIds.includes(s.currentUser.id)) return true;
    return !!s.currentUser?.groupId && t.assigneeGroupIds.includes(s.currentUser.groupId);
  };
  const tick = async (t: Todo) => {
    setBusyTodo(t.id);
    setTickError(null);
    try {
      await (callAction('setTodoDone', t.id, t.state !== 'done') as Promise<void>);
    } catch (e) {
      setTickError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyTodo(null);
    }
  };
  const toggleNotes = (id: string) =>
    setOpenNotes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const locate = (nodeId: NodeId) => {
    onLeave(); // the map has to be visible for this one
    callAction('revealNodeOnMap', nodeId);
  };
  const pick = (nodeId: NodeId) => {
    setSelected(nodeId);
    setOpenNode(null);
    setOpenPerson(null);
    setOpenNotes(new Set());
  };
  const rowsProps = {
    today,
    locale,
    mayTick,
    busyId: busyTodo,
    onTick: tick,
    openNotes,
    onToggleNotes: toggleNotes,
    nameOf,
  };

  /* ------------------------------------------------------------------ the tab ---- */

  const count = projects.length;
  return (
    <div id="dash-projects-tab">
      <div className="pj-lead">
        <p className="perm-section-lead">{projectText('lead', locale)}</p>
        <div className="pj-lead-actions">
          <span className="tgt-meta" id="pj-count">
            {count} {projectText(count === 1 ? 'projectCount' : 'projectsCount', locale)}
          </span>
          <button
            type="button"
            className="iconbtn"
            id="pj-refresh"
            title={projectText('refresh', locale)}
            onClick={() => setRefreshSeq((n) => n + 1)}
          >
            <svg className="ico">
              <use href="#i-undo" />
            </svg>
          </button>
        </div>
      </div>

      {error ? (
        <div className="pj-empty" id="pj-error">
          {projectText('loadError', locale)} ({error})
        </div>
      ) : loading && !payload ? (
        <div className="pj-empty">{projectText('loading', locale)}</div>
      ) : !projects.length ? (
        <div className="pj-strip" id="pj-strip">
          <div className="pj-empty">{projectText(canEditTodos(s.perm) ? 'noProjects' : 'noProjectsReadOnly', locale)}</div>
        </div>
      ) : (
        <>
          <div className="pj-strip" id="pj-strip">
            {summaries.map(({ project: p, summary: sm }) => {
              const node = s.nodes[p.nodeId];
              const on = p.nodeId === selectedId;
              return (
                <button
                  type="button"
                  key={p.nodeId}
                  className={`pj-chip${on ? ' on' : ''}`}
                  data-node={p.nodeId}
                  aria-pressed={on}
                  onClick={() => pick(p.nodeId)}
                >
                  <Pie pct={sm.pct} color="var(--accent)" size={30} text={false} />
                  <span className="pj-chip-meta">
                    <NodeChip node={node} isLight={s.isLight} />
                    <span className="pj-chip-sub">
                      <span className="pj-chip-frac">
                        {sm.counts.done}/{sm.counts.total}
                      </span>
                      {p.dueDate ? (
                        <span className={`pj-chip-due${sm.status === 'late' ? ' tone-bad' : ''}`}>
                          {projectText('target', locale)} {formatDue(p.dueDate, today)}
                        </span>
                      ) : null}
                      <StatusPill status={sm.status} locale={locale} />
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {current && selectedId && roll ? (
            <div className="pj-stack">
              {/* ---- the selected project ---- */}
              <div className="card pj-header" id="pj-header" data-node={selectedId} data-status={current.summary.status}>
                <div className="pj-hdr">
                  <span id="pj-pie">
                    <Pie pct={current.summary.pct} color="var(--accent)" />
                  </span>
                  <div className="pj-hdr-main">
                    <div className="pj-head-title">
                      <NodeChip node={s.nodes[selectedId]} isLight={s.isLight} labelId="pj-title" />
                      <StatusPill status={current.summary.status} locale={locale} id="pj-status" />
                      <span className="pj-crumb">
                        {roll.size - 1} {projectText('nodesBeneath', locale)}
                      </span>
                    </div>
                    <div className="pj-counts" id="pj-counts">
                      {(
                        [
                          ['open', current.summary.counts.open, ''],
                          ['done', current.summary.counts.done, 'tone-good'],
                          ['overdue', current.summary.counts.overdue, 'tone-bad'],
                          ['dueSoon', current.summary.counts.dueSoon, 'tone-warn'],
                          ['unassigned', current.summary.counts.unassigned, ''],
                        ] as const
                      ).map(([key, n, tone]) => (
                        <span key={key} className="dt-strip-chip pj-static" data-count={key}>
                          <span className={`dt-dot ${tone}`} />
                          {projectText(key, locale)} <b>{n}</b>
                        </span>
                      ))}
                    </div>
                  </div>
                  <button type="button" className="btn" id="pj-locate-selected" onClick={() => locate(selectedId)}>
                    <svg className="ico">
                      <use href="#i-target" />
                    </svg>
                    <span>{projectText('showInMap', locale)}</span>
                  </button>
                </div>
                <Schedule summary={current.summary} today={today} locale={locale} mayEdit={canEditTodos(s.perm)} />
              </div>

              {/* ---- the two views ---- */}
              <div className="perm-tabs sub" id="pj-views" role="tablist" aria-label="Project views">
                <button
                  type="button"
                  className={view === 'nodes' ? 'perm-tab on' : 'perm-tab'}
                  id="pj-view-nodes"
                  role="tab"
                  aria-selected={view === 'nodes'}
                  onClick={() => setView('nodes')}
                >
                  {projectText('viewNodes', locale)}
                </button>
                <button
                  type="button"
                  className={view === 'people' ? 'perm-tab on' : 'perm-tab'}
                  id="pj-view-people"
                  role="tab"
                  aria-selected={view === 'people'}
                  onClick={() => setView('people')}
                >
                  {projectText('viewPeople', locale)}
                </button>
              </div>
              {tickError ? (
                <div className="todo-error" id="pj-tick-error">
                  {tickError}
                </div>
              ) : null}

              {view === 'nodes' ? (
                <div className="card" id="pj-tree-card">
                  <div className="pj-card-head">
                    <h3>{projectText('viewNodes', locale)}</h3>
                    <span className="sp" />
                    <span className="tgt-meta">{projectText('nodesHint', locale)}</span>
                  </div>
                  {!tree.length ? (
                    <div className="pj-empty">{projectText('noTodos', locale)}</div>
                  ) : (
                    <div className="scroll">
                      <table className="utable" id="pj-tree">
                        <colgroup>
                          <col />
                          <col style={{ width: '26%' }} />
                          <col style={{ width: 72 }} />
                          <col style={{ width: 120 }} />
                          <col style={{ width: 150 }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>{projectText('colNode', locale)}</th>
                            <th>{projectText('colProgress', locale)}</th>
                            <th>{projectText('colDone', locale)}</th>
                            <th>{projectText('colNextDue', locale)}</th>
                            <th>{projectText('colAssignees', locale)}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tree.map(({ nodeId, depth }) => {
                            const r = roll.get(nodeId)!;
                            const node = s.nodes[nodeId];
                            const own = byNode.get(nodeId) ?? [];
                            const expandable = own.length > 0;
                            const open = openNode === nodeId;
                            return (
                              <Fragment key={nodeId}>
                                <tr
                                  className={`pj-node${open ? ' open' : ''}${expandable ? ' pj-expandable' : ''}`}
                                  data-node={nodeId}
                                  data-depth={depth}
                                  aria-expanded={expandable ? open : undefined}
                                  onClick={
                                    expandable
                                      ? (e) => {
                                          // The chip is the way to the node; a click on it is not a fold.
                                          if ((e.target as HTMLElement).closest('.aidoc-node')) return;
                                          setOpenNode(open ? null : nodeId);
                                        }
                                      : undefined
                                  }
                                >
                                  <td>
                                    <span className="pj-nodecell" style={{ paddingLeft: depth * 18 }}>
                                      <svg
                                        className={`todo-caret pj-node-caret${open ? ' open' : ''}${expandable ? '' : ' blank'}`}
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                      >
                                        <use href="#i-chev" />
                                      </svg>
                                      <NodeChip
                                        node={node}
                                        isLight={s.isLight}
                                        onClick={() => locate(nodeId)}
                                        title={`${projectText('showInMap', locale)}: ${node.label}`}
                                      />
                                      {projectIds.has(nodeId) && nodeId !== selectedId ? (
                                        <span className="pj-badge">{projectText('nestedProject', locale)}</span>
                                      ) : null}
                                      {own.length ? (
                                        <span className="pj-here">
                                          {own.length} {projectText('here', locale)}
                                        </span>
                                      ) : null}
                                    </span>
                                  </td>
                                  <td>
                                    <ProgressBar counts={r.subtree} />
                                  </td>
                                  <td className="pj-frac">
                                    {r.subtree.done}/{r.subtree.total}
                                  </td>
                                  <td>
                                    <DueCell due={r.nextDue} state="open" today={today} locale={locale} />
                                  </td>
                                  <td>
                                    <Avatars keys={r.assigneeKeys} nameOf={nameOf} locale={locale} />
                                  </td>
                                </tr>
                                {open ? (
                                  <tr className="pj-detail">
                                    <td colSpan={5}>
                                      <TodoRows todos={own} emptyText={projectText('noOwnTodos', locale)} {...rowsProps} />
                                    </td>
                                  </tr>
                                ) : null}
                              </Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <div className="card" id="pj-assignees-card">
                  <div className="pj-card-head">
                    <h3>{projectText('viewPeople', locale)}</h3>
                    <span className="sp" />
                    <span className="tgt-meta">{projectText('peopleHint', locale)}</span>
                  </div>
                  {!people.length ? (
                    <div className="pj-empty">{projectText('noTodos', locale)}</div>
                  ) : (
                    <div className="scroll">
                      <table className="utable" id="pj-assignees">
                        <colgroup>
                          <col />
                          <col style={{ width: '30%' }} />
                          <col style={{ width: 72 }} />
                          <col style={{ width: 90 }} />
                          <col style={{ width: 120 }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>{projectText('colWho', locale)}</th>
                            <th>{projectText('colProgress', locale)}</th>
                            <th>{projectText('colDone', locale)}</th>
                            <th>{projectText('colOverdue', locale)}</th>
                            <th>{projectText('colNextDue', locale)}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {people.map((row) => {
                            const open = openPerson === row.key;
                            const name = nameOf(row.key);
                            const list = scopeTodos.filter((t) =>
                              row.kind === 'unassigned'
                                ? !t.assigneeUserIds.length && !t.assigneeGroupIds.length
                                : row.kind === 'group'
                                  ? t.assigneeGroupIds.includes(row.id!)
                                  : t.assigneeUserIds.includes(row.id!),
                            );
                            return (
                              <Fragment key={row.key}>
                                <tr
                                  className={`pj-assignee${open ? ' open' : ''}`}
                                  data-key={row.key}
                                  aria-expanded={open}
                                  onClick={() => setOpenPerson(open ? null : row.key)}
                                >
                                  <td>
                                    <span className="pj-nodecell">
                                      <svg className={`todo-caret pj-node-caret${open ? ' open' : ''}`} viewBox="0 0 24 24" aria-hidden="true">
                                        <use href="#i-chev" />
                                      </svg>
                                      <span
                                        className={`subj-av${row.kind === 'group' ? ' grp' : ''}${row.kind === 'unassigned' ? ' pj-av-none' : ''}`}
                                      >
                                        {row.kind === 'unassigned' ? '?' : name ? name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase() : '?'}
                                      </span>
                                      <span>
                                        {name}
                                        {row.kind === 'group' ? <span className="pj-muted"> · {projectText('team', locale)}</span> : null}
                                      </span>
                                    </span>
                                  </td>
                                  <td>
                                    <ProgressBar counts={row.counts} />
                                  </td>
                                  <td className="pj-frac">
                                    {row.counts.done}/{row.counts.total}
                                  </td>
                                  <td className={row.counts.overdue ? 'tone-bad pj-overdue' : 'pj-muted pj-overdue'}>
                                    {row.counts.overdue || '—'}
                                  </td>
                                  <td>
                                    <DueCell due={row.nextDue} state="open" today={today} locale={locale} />
                                  </td>
                                </tr>
                                {open ? (
                                  <tr className="pj-detail">
                                    <td colSpan={5}>
                                      <TodoRows
                                        todos={list}
                                        emptyText={projectText('noPersonTodos', locale)}
                                        nodeLabelOf={nodeLabelOf}
                                        {...rowsProps}
                                      />
                                    </td>
                                  </tr>
                                ) : null}
                              </Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ---- the burn-up ---- */}
              <div className="card" id="pj-burnup-card">
                <div className="pj-card-head">
                  <h3>{projectText('burnupTitle', locale)}</h3>
                  <span className="sp" />
                  <span className="pj-legend">
                    <span>
                      <i style={{ background: 'var(--accent)' }} />
                      {projectText('created', locale)}
                    </span>
                    <span>
                      <i style={{ background: 'var(--review-approved)' }} />
                      {projectText('completed', locale)}
                    </span>
                  </span>
                </div>
                {range && points.length ? (
                  <Burnup points={points} from={range.from} to={range.to} dueDate={current.project.dueDate} today={today} locale={locale} />
                ) : (
                  <div className="pj-empty">{projectText('burnupEmpty', locale)}</div>
                )}
                <div className="tgt-meta pj-hint">{projectText('burnupHint', locale)}</div>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
